@if (mode!==ConsentDialogMode.None) {
  <div class="backdrop"></div>
  <!-- Cookies consent dialog - custom structure for Figma design -->
  @if (mode===ConsentDialogMode.Cookies) {
    <div class="cookies-dialog-wrapper" [class.customize-mode]="customizingCookies"
      >
      <!-- Header -->
      <div class="dialog-header">
        <h3>{{ customizingCookies ? LL.consent.cookies.customizeHeader() : LL.consent.cookies.header() }}</h3>
      </div>
      <!-- Body - Non-customize mode -->
      @if (!customizingCookies) {
        <div class="dialog-body">
          <p class="message">
            {{ LL.consent.cookies.message() }}
            <a [href]="privacyUrl" target="_blank">{{ LL.consent.cookies.privacyPolicy() }}</a>.
          </p>
          <!-- why Advertising is off (#5682). Shown on this first view too, because
               "Accept All Cookies" lives here: someone who presses it should already
               know their browser's signal is what kept Advertising off. -->
          @if (showGpcNotice) {
            <p class="gpc-notice" id="gpcNotice">{{ LL.consent.cookies.types.advertising.gpcNotice() }}</p>
          }
        </div>
      }
      <!-- Body - Customize mode -->
      @if (customizingCookies) {
        <div class="dialog-body">
          <div class="options-container">
            @for (c of allCookieConsentTypes; track c; let last = $last) {
              <div class="cookie-option" [class.last]="last">
                <div class="option-content">
                  <span class="option-title">
                    {{c.getName()}}{{c.required ? ' (' + LL.consent.cookies.required() + ')' : ''}}
                  </span>
                  <p class="option-description">{{c.getDescription()}}</p>
                  @if (c.id === 'ad' && showGpcNotice) {
                    <p class="gpc-notice" id="gpcNoticeAdvertising">{{ LL.consent.cookies.types.advertising.gpcNotice() }}</p>
                  }
                </div>
                <cs-toggle [(checked)]="cookieConsentList[c.id]" [disabled]="c.required"></cs-toggle>
              </div>
            }
          </div>
          <!-- an unanswered category shows its toggle neutral, and Save Settings
               stays disabled until each one is set (#5712). Say so, or a disabled
               button with no explanation reads as a broken screen. -->
          @if (!canSaveCookieChoices) {
            <p class="choose-each" id="chooseEachCookie">{{ LL.consent.cookies.chooseEach() }}</p>
          }
        </div>
      }
      <!-- Footer -->
      <div class="dialog-footer">
        <!-- Reject All is on both views: refusing must take no more clicks than
             accepting, or EU regulators do not count an "Accept All" as consent.
             Both views lay their three buttons out the same way — see the
             footer's layout rule in consent-dialog.scss. -->
        <cs-button class="btn-reject" id="rejectAllCookies" medium secondary (click)="handleRejectAll()"
          [label]="LL.consent.cookies.rejectAll()">
        </cs-button>
        <cs-button class="btn-secondary" medium secondary
          (click)="customizingCookies ? handleAcceptCustomized() : handleCustomize()"
          [disabled]="customizingCookies && !canSaveCookieChoices"
          [label]="customizingCookies ? LL.consent.cookies.saveSettings() : LL.consent.cookies.customize()">
        </cs-button>
        <cs-button class="btn-primary" medium primary (click)="handleAcceptAll()"
          [label]="LL.consent.cookies.acceptAll()">
        </cs-button>
      </div>
    </div>
  }
  <!-- User/License consent dialog - Figma design -->
  @if (mode===ConsentDialogMode.User) {
    <div class="license-dialog-wrapper">
      <!-- Body. The header is inside it, so it scrolls away with the list: pinned, a
           two-line title and a paragraph took the top third of a phone (#5813). -->
      <div class="dialog-body">
        <!-- An existing user asked again because the text changed gets a header that
             says so (#5587). Reading "Terms & Agreements" out of nowhere years after
             signing up looks like something is wrong with the account. -->
        <div class="dialog-header">
          <h3>{{ consentTextUpdated ? LL.consent.user.updatedHeader() : LL.consent.user.header() }}</h3>
          <p class="subtitle">{{ consentTextUpdated ? LL.consent.user.updatedSubtitle() : LL.consent.user.subtitle() }}</p>
        </div>
        <!-- Required items -->
        <div class="consent-list">
          @for (c of userConsentTypes; track c) {
            @if (!c.optional) {
              <cs-item>
                <cs-checkbox item-left [(checked)]="userConsentAccepts[c.id]" [disabled]="processingTerms"></cs-checkbox>
                <cs-label>
                  <span class="consent-label">{{consentLabel(c)}}</span>
                  <a class="details-link" not-capture [href]="apiOrigin + c.url" target="_blank" rel="noopener noreferrer" (click)="openConsentDetails($event, c.url)">{{ LL.consent.user.details() }}</a>
                </cs-label>
              </cs-item>
            }
          }
        </div>
        <!-- Additional options section -->
        <div class="additional-options-section">
          <span class="additional-options-label">{{ LL.consent.user.additionalOptions() }}</span>
          <div class="consent-list">
            @for (c of userConsentTypes; track c) {
              @if (c.optional) {
                <cs-item>
                  <cs-checkbox item-left [(checked)]="userConsentAccepts[c.id]" [disabled]="processingTerms"></cs-checkbox>
                  <cs-label>
                    <span class="consent-label">{{consentLabel(c)}}</span>
                    <a class="details-link" not-capture [href]="apiOrigin + c.url" target="_blank" rel="noopener noreferrer" (click)="openConsentDetails($event, c.url)">{{ LL.consent.user.details() }}</a>
                  </cs-label>
                </cs-item>
              }
            }
          </div>
        </div>
        <!-- Separator and Accept All -->
        <div class="accept-all-section">
          <div class="separator"></div>
          <cs-item class="accept-all">
            <cs-checkbox item-left [checked]="agreeAll" (checkedChange)="handleAgreeAllChanged()" [disabled]="processingTerms"></cs-checkbox>
            <cs-label>{{ LL.consent.user.acceptAll() }}</cs-label>
          </cs-item>
        </div>
        <!-- Error message -->
        @if (showRequiredError) {
          <p class="required-error">{{ LL.consent.user.requiredError() }}</p>
        }
        @if (persistErrorMessage) {
          <p class="required-error">{{ persistErrorMessage }}</p>
        }
      </div>
      <!-- Footer -->
      <div class="dialog-footer">
        <!-- Later is offered only while the person opened this screen themselves
             from the home card. Once the deadline passes the screen is raised by
             the app and there is nothing to go back to. -->
        @if (reviewingTerms) {
          <cs-button class="btn-secondary" medium secondary id="laterTerms"
            [disabled]="processingTerms" (click)="handleTermsLater()"
            [label]="LL.consent.user.later()">
          </cs-button>
        }
        <cs-button class="btn-primary" medium primary (click)="handleTermsProceed()" id="proceedTerms"
          [disabled]="processingTerms" [label]="LL.consent.user.continue()">
          @if (processingTerms) {
            <cs-spinner></cs-spinner>
          }
        </cs-button>
      </div>
    </div>
  }
}
